/**
 * Out-chip actions (#1268 follow-up) — click an "Out — Confined Space" chip or
 * lane badge and move or clear that one day right there.
 *
 * Before this, the chip was inert. The only way to change a block was the
 * helper bench's "Mark someone out" dialog, and moving a day meant clearing it
 * and typing it in again. A dispatcher looking at "Out" on the board on the
 * morning plans changed could not find either.
 *
 * PTO / call-in / NCNS days get the same click but no buttons: those belong
 * to the request or call-in that wrote them (see routes/outDays.ts).
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarOff } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { describeRePaired, formatOutDay, type RePaired } from "./out-days";

export interface OutDayTarget {
    userId: string;
    name: string;
    /** YYYY-MM-DD — the day the board is showing. */
    date: string;
    label: string;
    reason: string;
    clearable: boolean;
}

interface OutDayActionsDialogProps {
    target: OutDayTarget | null;
    onClose: () => void;
}

interface MoveResponse {
    fromDate: string;
    toDate: string;
    label: string;
    rePaired: RePaired[];
}
interface ClearResponse {
    cleared: string[];
    rePaired: RePaired[];
}

const apiMessage = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong.");

export function OutDayActionsDialog({ target, onClose }: OutDayActionsDialogProps) {
    const qc = useQueryClient();
    const [toDate, setToDate] = useState("");
    const [moveError, setMoveError] = useState<string | null>(null);

    // Re-seed per chip: a date typed for yesterday's chip is a wrong entry today.
    useEffect(() => {
        setToDate("");
        setMoveError(null);
    }, [target?.userId, target?.date]);

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["dispatch", "out-days"] });
        qc.invalidateQueries({ queryKey: ["dispatch", "pairings"] });
    };

    const move = useMutation({
        mutationFn: (v: { userId: string; fromDate: string; toDate: string }) =>
            api.post<MoveResponse>("/api/dispatch/out-days/move", v),
        onSuccess: (r, v) => {
            const first = target?.name.split(" ")[0] ?? "They";
            const rePaired = describeRePaired(first, r.rePaired);
            toast.success(`${r.label} moved to ${formatOutDay(r.toDate)}.${rePaired ? ` ${rePaired}` : ""}`, {
                action: {
                    label: "Undo",
                    // Straight to the API: the dialog is closed by now, so an
                    // error has to surface as a toast, not inside it.
                    onClick: () => {
                        api.post("/api/dispatch/out-days/move", {
                            userId: v.userId,
                            fromDate: v.toDate,
                            toDate: v.fromDate,
                        })
                            .then(invalidate)
                            .catch((e) => toast.error(`Could not undo: ${apiMessage(e)}`));
                    },
                },
            });
            invalidate();
            onClose();
        },
        // Shown inside the dialog, not as a toast: the dispatcher is still
        // looking at the date field and needs to pick another day.
        onError: (e) => setMoveError(apiMessage(e)),
    });

    const clear = useMutation({
        mutationFn: (t: OutDayTarget) =>
            api.delete<ClearResponse>(
                `/api/dispatch/out-days?userId=${encodeURIComponent(t.userId)}&startDate=${encodeURIComponent(
                    t.date,
                )}&endDate=${encodeURIComponent(t.date)}`,
            ),
        onSuccess: (r, t) => {
            const first = t.name.split(" ")[0];
            const rePaired = describeRePaired(first, r.rePaired);
            toast.success(
                `${t.label} cleared. ${first} is on the schedule for ${formatOutDay(t.date)}.${rePaired ? ` ${rePaired}` : ""}`,
                {
                    action: {
                        label: "Undo",
                        onClick: () => {
                            api.post("/api/dispatch/out-days", {
                                userId: t.userId,
                                startDate: t.date,
                                endDate: t.date,
                                reason: t.reason,
                                label: t.label,
                                includeWeekends: true,
                            })
                                .then(invalidate)
                                .catch((e) => toast.error(`Could not undo: ${apiMessage(e)}`));
                        },
                    },
                },
            );
            invalidate();
            onClose();
        },
        onError: (e) => toast.error(`Could not clear: ${apiMessage(e)}`),
    });

    const t = target;
    const busy = move.isPending || clear.isPending;
    const first = t?.name.split(" ")[0] ?? "";
    const owner = t?.reason === "pto" ? "an approved PTO request" : "a call-in";

    return (
        <Dialog open={Boolean(t)} onClose={onClose} title={t ? `${t.name} is out ${formatOutDay(t.date)}` : "Out"}>
            {t && (
                <div className="space-y-4 p-5" data-testid="out-day-actions">
                    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <CalendarOff className="h-4 w-4 shrink-0" />
                        {t.label}
                    </p>

                    {t.clearable ? (
                        <>
                            <form
                                className="flex flex-wrap items-end gap-2"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!toDate || busy) return;
                                    setMoveError(null);
                                    move.mutate({ userId: t.userId, fromDate: t.date, toDate });
                                }}
                            >
                                <label className="grid gap-1 text-xs text-muted-foreground" htmlFor="out-move-to">
                                    Move to
                                    <Input
                                        id="out-move-to"
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => {
                                            setToDate(e.target.value);
                                            setMoveError(null);
                                        }}
                                        className="w-auto"
                                    />
                                </label>
                                <Button type="submit" disabled={!toDate || toDate === t.date || busy}>
                                    {move.isPending ? "Moving…" : "Move"}
                                </Button>
                            </form>
                            {moveError && (
                                <p
                                    role="alert"
                                    className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                                    data-testid="out-move-error"
                                >
                                    {moveError}
                                </p>
                            )}
                            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
                                <Button variant="outline" disabled={busy} onClick={() => clear.mutate(t)}>
                                    {clear.isPending ? "Clearing…" : "Clear: back on the schedule"}
                                </Button>
                                <Button variant="ghost" onClick={onClose}>
                                    Close
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Moving keeps the label. If {first} is a helper set to work alone that day, clearing or
                                moving puts them back with their usual lead.
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
                                This day comes from {owner}. To change it, edit or cancel that{" "}
                                {t.reason === "pto" ? "request in Time Off" : "call-in"}.
                            </p>
                            <div className="flex justify-end">
                                <Button variant="ghost" onClick={onClose}>
                                    Close
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Dialog>
    );
}
